import { FC, useState, useEffect, useContext } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type {
  Product,
  ProductImage,
  ProductVariant,
} from '@commerce/types/product'
import ProductDescription from '../ProductDescription/ProductDescription'
import { ReviewsIo, StepIndicator } from '@components/yadav/ui'
import RelatedProducts from '../RelatedProducts'
import { Container, Modal } from '@components/ui'
import { SEO } from '@components/common'
import {
  getProductVariant,
  selectDefaultOptionFromProduct,
  SelectedOptions,
  slugContainsMetalOption,
  hasValidMetalOptionSlug,
  selectMetalOptionFromProductSlug,
  setProductName,
} from '../helpers'
import { InovaTool } from '@components/yadav/common'
import ProductBreadcrumbs from '../ProductBreadcrumbs/ProductBreadcrumbs'
import inovaHand from '@publicAssets/images/ui/product-view/inovaHand.png'
import InteractiveViewImage from '@publicAssets/images/ui/product-view/InteractiveViewImage.webp'
import TryOnHandIcon from '@publicAssets/images/ui/product-view/TryOnHandIcon.svg'
import InteractiveViewIcon from '@publicAssets/images/ui/product-view/InteractiveViewIcon.svg'
import Carousel from '@components/ui/Carousel'
import { AlertContext } from 'context/alertContext'
import cn from 'clsx'
import { MagnifyngGlass, PointerHand } from '@components/yadav/icons'
import ProductSlider from './ProductSlider'
import s from './ProductView.module.css'
import { imageLoader } from '@utils/image-loader'

const YdBenefits = dynamic(() => import('@components/yadav/ui/YdBenefits'))

const OrderAndFinancing = dynamic(
  () => import('@components/yadav/ui/OrderAndFinancing')
)

const QualityAndProcess = dynamic(
  () => import('@components/yadav/ui/QualityAndProcess')
)
interface ProductViewProps {
  product: Product
  relatedProducts: Product[]
}

type ImagesForCarousel = {
  src: string
  alt: string
}[]

const placeholderImg = '/product-img-placeholder.svg'

const ProductView: FC<ProductViewProps> = ({ product, relatedProducts }) => {
  const router = useRouter()
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const [imagesForCarousel, setImagesForCarousel] = useState<ImagesForCarousel>(
    []
  )
  const [imagesForDesktop, setImagesForDesktop] = useState<ProductImage[]>([])
  const [prevScrollPos, setPrevScrollPos] = useState(0)
  const [productVariant, setProductVariant] = useState<
    ProductVariant | undefined
  >()
  const [startSetting, setStartSetting] = useState('setting')
  const { setShowMessage } = useContext(AlertContext)
  const [showVideo, setShowVideo] = useState(false)
  const [displayModal, setDisplayModal] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  useEffect(() => {
    if (productVariant) {
      const images = productVariant.images.map((image, index) => {
        return {
          src: image.url || placeholderImg,
          alt: image.alt || `Product Image ${index + 1}`,
        }
      })
      setImagesForCarousel(images)

      if (
        productVariant.videos &&
        productVariant.videos.length > 0 &&
        (productVariant.videos[0].local_url ||
          productVariant.videos[0].alternative_url)
      ) {
        setImagesForDesktop(productVariant.images)
      } else {
        // If the variant does not have a video, trim the first image.
        setImagesForDesktop(productVariant.images.slice(1))
      }
    }
  }, [productVariant])

  useEffect(() => {
    try {
      let diamondLocalStorage = localStorage.getItem('diamond')
      if (diamondLocalStorage) {
        setStartSetting('diamond')
      } else {
        setStartSetting('setting')
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    const regexPathQuery = /\?.*$/ // Match the query parameters starting from '?'
    const slug = router.asPath.split('/')[2].replace(regexPathQuery, '')
    // Check if the slug belongs to the master variant (master_variant_slug === product_slug)
    if (slug === product.slug) {
      selectDefaultOptionFromProduct(product, setSelectedOptions)
    } else {
      if (!slugContainsMetalOption(slug)) {
        selectDefaultOptionFromProduct(product, setSelectedOptions)
      } else {
        if (hasValidMetalOptionSlug(product, slug)) {
          selectMetalOptionFromProductSlug(product, setSelectedOptions, slug)
        } else {
          selectDefaultOptionFromProduct(product, setSelectedOptions)
        }
      }
    }
  }, [product, router])

  useEffect(() => {
    setProductVariant(() => getProductVariant(product, selectedOptions))
  }, [product, selectedOptions])

  useEffect(() => {
    let intervalID: NodeJS.Timer
    const { mode } = router.query

    intervalID = setTimeout(() => {
      if (productVariant && mode) {
        window.history.replaceState(
          {
            ...window.history.state,
            as: `/product/${productVariant.slug}?mode=${mode}`,
            url: `/product/${productVariant.slug}?mode=${mode}`,
          },
          '',
          `/product/${productVariant.slug}?mode=${mode}`
        )
      } else if (productVariant && !mode) {
        window.history.replaceState(
          {
            ...window.history.state,
            as: `/product/${productVariant.slug}`,
            url: `/product/${productVariant.slug}`,
          },
          '',
          `/product/${productVariant.slug}`
        )
      }
    }, 1000)

    return () => {
      clearInterval(intervalID)
    }
  }, [productVariant, router.query])

  useEffect(() => {
    if (productVariant && window.gtag) {
      window.gtag('event', 'view_item', {
        currency: productVariant.price.currencyCode || 'USD',
        value: productVariant.price.value,
        items: [
          {
            item_id: productVariant.sku,
            item_name: productVariant.name,
            affiliation: 'Yadav Diamonds and Jewelry',
            item_brand: 'Yadav Diamonds and Jewelry',
            item_category: product.taxons.find((taxon) => taxon.isLeaf)?.name,
            item_variant: productVariant.options[0]?.values[0]?.label,
            location_id: `${process.env.showroomPlaceId}`,
            price: productVariant.price.value,
            quantity: 1,
          },
        ],
      })
    }
  }, [product.taxons, productVariant])

  /* Function to remove empty space created by inova feature */
  const removeEmptySpace = () => {
    const element = document.querySelector(
      'span[style="display: block; width: 55px; height: 55px;"]'
    )
    if (element instanceof HTMLElement) {
      // change height and width to 0
      element.style.height = '0'
      element.style.width = '0'
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY === 0) return removeEmptySpace()
      const currentScrollPos = window.pageYOffset
      // if current post is less than 100 we avoid to hide the navbar
      if (prevScrollPos > currentScrollPos || currentScrollPos < 150) {
        removeEmptySpace()
      } else {
        removeEmptySpace()
      }
      setPrevScrollPos(currentScrollPos)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [prevScrollPos])
  /* */

  const handleOpenInova = (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault()
    if (typeof window !== 'undefined') {
      try {
        window.cls_InovaDiamonds_Iframe.Inova__OpenFrame()
      } catch (error) {
        setShowMessage({
          show: true,
          message:
            'An error occurred when opening the virtual ring tool. Please try again later.',
          type: 'Error',
        })
      }
    }
  }

  return (
    <div>
      {product.cyo_type === 'setting' && (
        <StepIndicator
          className="sm:my-4 sm:mx-auto sm:max-w-7xl w-full"
          startWith={startSetting}
        />
      )}
      {product &&
        product.taxons &&
        product.taxons.length > 0 &&
        product.taxons.some((taxon) => taxon.isLeaf) && (
          <ProductBreadcrumbs
            product={product}
            className="mb-6 px-3 sm:my-4 sm:mx-auto sm:max-w-7xl w-full"
          />
        )}
      <Container>
        <div className="flex flex-col my-5 lg:flex-row lg:gap-4 xl:gap-0 lg:justify-between">
          <div className="lg:w-[60%] w-full relative">
            {product.properties &&
              product.properties.length > 0 &&
              product.properties.some(
                (property) =>
                  property.name.toLowerCase() === 'augmented-reality' &&
                  property.value?.toLowerCase() === 'yes'
              ) && (
                <div className="invisible">
                  <InovaTool sku={productVariant?.sku || product.sku || ''} />
                </div>
              )}
            {productVariant &&
              productVariant.images &&
              productVariant.images.length > 0 && (
                <div>
                  {/* Desktop */}
                  <div className="hidden sm:grid sm:grid-cols-2 sm:gap-4 sm:w-full max-w-[744px] sm:mx-auto">
                    <div className="relative col-span-2 w-full max-w-[744px] h-[744px] border border-gray-light-3">
                      {product.properties &&
                        product.properties.length > 0 &&
                        product.properties.some(
                          (property) =>
                            property.name.toLowerCase() ===
                              'augmented-reality' &&
                            property.value?.toLowerCase() === 'yes'
                        ) && (
                          <>
                            <button
                              className="absolute hidden right-5 top-5 px-4 py-2 bg-[#f7f7f7] hover:bg-primary z-10"
                              id="try_vto_5"
                              onClick={() => {
                                if (window && window.cls_InovaDiamonds_Iframe) {
                                  window.cls_InovaDiamonds_Iframe.Inova__OpenFrame(
                                    'm3d'
                                  )
                                }
                              }}
                            >
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-5 flex items-center justify-center">
                                  <Image
                                    width={17}
                                    height={17}
                                    priority
                                    alt=""
                                    src={InteractiveViewIcon}
                                    objectFit="cover"
                                  />
                                </div>
                                <span>Interactive View</span>
                              </div>
                            </button>
                            <button
                              className="absolute hidden bottom-5 left-5 px-4 py-2 bg-[#f7f7f7] hover:bg-primary z-10"
                              id="try_vto_1"
                              onClick={(e) => handleOpenInova(e)}
                            >
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-5 flex items-center justify-center">
                                  <Image
                                    width={18}
                                    height={15}
                                    priority
                                    alt=""
                                    src={TryOnHandIcon}
                                    objectFit="cover"
                                  />
                                </div>
                                <span>Virtual Ring Try On</span>
                              </div>
                            </button>
                          </>
                        )}
                      <div className="group">
                        {productVariant.videos &&
                        productVariant.videos.length > 0 &&
                        (productVariant.videos[0].local_url ||
                          productVariant.videos[0].alternative_url) ? (
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
                            <Image
                              priority={true}
                              loader={imageLoader}
                              src={
                                productVariant.images[0].url || placeholderImg
                              }
                              alt={
                                productVariant.images[0].alt || 'Product Image'
                              }
                              layout="fill"
                              objectFit="contain"
                              sizes="(max-width: 768px) 1200px, (max-width: 1200px) 2400px, 2000px"
                              className={cn(
                                { visible: !showVideo },
                                { invisible: showVideo }
                              )}
                            />
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full lg:w-full">
                              <video
                                src={
                                  productVariant.videos[0].local_url ||
                                  productVariant.videos[0].alternative_url ||
                                  ''
                                }
                                muted
                                autoPlay
                                loop
                                className={cn(
                                  'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full',
                                  { visible: showVideo },
                                  { invisible: !showVideo }
                                )}
                                onLoadedData={() => setShowVideo(true)}
                                playsInline
                              >
                                <source
                                  src={
                                    productVariant.videos[0].local_url ||
                                    productVariant.videos[0].alternative_url ||
                                    ''
                                  }
                                  type="video/mp4"
                                />
                              </video>
                            </div>
                          </div>
                        ) : (
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
                            <Image
                              priority={true}
                              loader={imageLoader}
                              src={
                                productVariant.images[0].url || placeholderImg
                              }
                              alt={
                                productVariant.images[0].alt || 'Product Image'
                              }
                              layout="fill"
                              objectFit="contain"
                              sizes="(max-width: 768px) 1200px, (max-width: 1200px) 2400px, 2000px"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {imagesForDesktop.map((image, i) => (
                      <div
                        key={image.url}
                        className="relative col-span-1 w-full max-w-[367px] h-[367px] border border-gray-light-3 flex justify-center items-center group cursor-pointer"
                        onClick={() => {
                          setCurrentSlide(i + 1)
                          setDisplayModal(true)
                        }}
                      >
                        <Image
                          priority={true}
                          loader={imageLoader}
                          src={image.url || placeholderImg}
                          alt={image.alt || 'Product Image'}
                          layout="fill"
                          objectFit="cover"
                        />
                        <button
                          type="button"
                          className="hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 group-hover:block"
                        >
                          <MagnifyngGlass className="w-10 h-10" />
                        </button>
                      </div>
                    ))}
                    {product.properties &&
                      product.properties.length > 0 &&
                      product.properties.some(
                        (property) =>
                          property.name.toLowerCase() === 'augmented-reality' &&
                          property.value?.toLowerCase() === 'yes'
                      ) && (
                        <>
                          <div className="hidden relative col-span-1 w-full max-w-[367px] h-[367px] border border-gray-light-3 hover:cursor-pointer z-10">
                            <div
                              id="try_vto_2"
                              role="button"
                              onClick={(e) => handleOpenInova(e)}
                            >
                              <Image
                                priority={true}
                                src={inovaHand}
                                alt="Inova try on"
                                layout="fill"
                                objectFit="contain"
                              />
                            </div>
                            <div className="hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 group-hover:block">
                              <PointerHand />
                            </div>
                          </div>
                          <div className="hidden relative col-span-1 w-full max-w-[367px] h-[367px] border border-gray-light-3 hover:cursor-pointer z-10">
                            <div
                              id="try_vto_6"
                              role="button"
                              onClick={() => {
                                if (window && window.cls_InovaDiamonds_Iframe) {
                                  window.cls_InovaDiamonds_Iframe.Inova__OpenFrame(
                                    'm3d'
                                  )
                                }
                              }}
                            >
                              <Image
                                priority={true}
                                src={InteractiveViewImage}
                                alt="Interactive View"
                                layout="fill"
                                objectFit="cover"
                              />
                            </div>
                          </div>
                        </>
                      )}
                  </div>
                  {/* Mobile */}
                  <div className="sm:hidden">
                    {imagesForCarousel &&
                      imagesForCarousel &&
                      imagesForCarousel.length > 0 && (
                        <div className="sm:hidden block h-[480px] w-[340px] relative mx-auto overflow-hidden">
                          <Carousel
                            data={imagesForCarousel}
                            width={360}
                            height={360}
                            centerMode={false}
                            infinite={false}
                            speed={500}
                            slidesToShow={1}
                            slidesToScroll={1}
                            dots={true}
                            productVariant={productVariant}
                            arrows={false}
                          />
                        </div>
                      )}
                    <div className="absolute flex justify-center top-[74%] w-full gap-6">
                      <button
                        className="hidden px-2 py-2 bg-[#f7f7f7] hover:bg-primary w-[169px] z-10"
                        id="try_vto_7"
                        onClick={() => {
                          if (window && window.cls_InovaDiamonds_Iframe) {
                            window.cls_InovaDiamonds_Iframe.Inova__OpenFrame(
                              'm3d'
                            )
                          }
                        }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 flex items-center justify-center">
                            <Image
                              width={16}
                              height={16}
                              priority
                              alt=""
                              src={InteractiveViewIcon}
                              objectFit="cover"
                            />
                          </div>
                          <span className="whitespace-nowrap">
                            Interactive View
                          </span>
                        </div>
                      </button>
                      <button
                        className="hidden px-2 py-2 bg-[#f7f7f7] hover:bg-primary w-[169px] z-10"
                        id="try_vto_8"
                        onClick={(e) => handleOpenInova(e)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 flex items-center justify-center">
                            <Image
                              width={15}
                              height={12}
                              priority
                              alt=""
                              src={TryOnHandIcon}
                              objectFit="cover"
                            />
                          </div>
                          <span className="whitespace-nowrap">
                            Virtual Ring Try On
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
          {productVariant && (
            <ProductDescription
              product={product}
              productVariant={productVariant}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
          )}
        </div>
      </Container>
      <div className="w-full mt-[112px]">
        <YdBenefits className="bg-accent-9" isHome={false} />
      </div>
      <Container>
        {relatedProducts && relatedProducts.length > 0 && (
          <RelatedProducts relatedProducts={relatedProducts} />
        )}
      </Container>
      <div className="mt-[112px]">
        <OrderAndFinancing price={product?.price?.value || 0} />
      </div>
      <div className="mt-[112px]">
        <QualityAndProcess />
      </div>
      <div className="w-full bg-neutral-50 pt-20 pb-9 mb-28">
        <ReviewsIo product={product} />
      </div>
      {displayModal && productVariant && (
        <Modal
          onClose={() => setDisplayModal(false)}
          containerClassName={s.modal}
        >
          <ProductSlider
            productVariant={productVariant}
            currentSlide={currentSlide}
          />
        </Modal>
      )}
      <SEO
        title={setProductName(product, productVariant)}
        description={
          productVariant &&
          productVariant.slug ===
            'hailey-eternity-band-lab-grown-white-gold-14k'
            ? 'Discover the Hailey Eternity Band in lab-grown white gold, featuring a continuous line of shimmering diamonds. Perfect for celebrating timeless love and elegance.'
            : productVariant && productVariant.slug === 'the-bezel-ring-white-gold-14k'
            ? 'Explore the sleek and contemporary Bezel Ring in 14K white gold, featuring a secure diamond setting that blends modern style with lasting elegance.'
            : productVariant && productVariant.slug === 'the-bezel-ring-yellow-gold-18k'
            ? 'Discover the sophisticated Bezel Ring in 18K yellow gold at Yadav Jewelry. Its minimalist design ensures the diamond shines brightly in a timeless setting.'
            : productVariant && productVariant.slug === 'the-calla-ring-yellow-gold-18k'
            ? 'The Calla Ring from Yadav Jewelry combines graceful curves and a stunning center diamond, making it the perfect symbol of elegance and beauty.'
            : productVariant && productVariant.slug === 'the-thistle-ring-yellow-gold-14k'
            ? 'The Thistle Ring in 14K yellow gold offers a charming and unique design, combining intricate detailing with the brilliance of a central diamond.'
            : productVariant && productVariant.slug === 'the-thistle-ring-yellow-gold-18k'
            ? 'Shop the Thistle Ring in 18K yellow gold, featuring a nature-inspired design that beautifully complements a sparkling diamond.'
            : product.description
        }
        robots={
          product.cyo_type && product.cyo_type.toLowerCase() === 'gemstone'
            ? 'noindex'
            : undefined
        }
        openGraph={{
          type: 'website',
          title: setProductName(product, productVariant),
          description: product.description,
          images: [
            {
              url:
                productVariant &&
                productVariant.images &&
                productVariant.images.length > 0
                  ? productVariant.images[0].url
                  : placeholderImg,
              width: '800',
              height: '600',
              alt: setProductName(product, productVariant),
            },
          ],
        }}
      />
    </div>
  )
}

export default ProductView
